import type { OptimizationJobRunnerProgress } from '../types/optimizationJobRunnerProgress';

function unwrapOptimizationJobRow(row: unknown): Record<string, unknown> {
  if (!row || typeof row !== 'object') return {};
  let cur = row as Record<string, unknown>;
  for (let depth = 0; depth < 3; depth += 1) {
    const inner = cur.data;
    if (!inner || typeof inner !== 'object' || Array.isArray(inner)) break;
    const record = inner as Record<string, unknown>;
    if (
      'id' in record ||
      'status' in record ||
      'progress_percent' in record ||
      'steps' in record ||
      'runner_progress' in record
    ) {
      cur = record;
    } else {
      break;
    }
  }
  return cur;
}

function readPositiveInt(value: unknown): number | undefined {
  if (value == null || value === '') return undefined;
  const n = typeof value === 'string' ? parseInt(value, 10) : Number(value);
  if (!Number.isFinite(n) || n < 0) return undefined;
  return Math.floor(n);
}

function readRunnerProgressPercent(value: unknown): number | undefined {
  if (value == null || value === '') return undefined;
  const n = typeof value === 'string' ? parseFloat(value) : Number(value);
  if (!Number.isFinite(n)) return undefined;
  return Math.max(0, Math.min(100, n));
}

export function parseOptimizationJobRunnerProgress(job: unknown): OptimizationJobRunnerProgress | null {
  const row = unwrapOptimizationJobRow(job);
  const raw = row.runner_progress;
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;

  const rp = raw as Record<string, unknown>;
  const parsed: OptimizationJobRunnerProgress = {
    iteration: readPositiveInt(rp.iteration),
    max_iterations: readPositiveInt(rp.max_iterations),
    evaluated_candidates: readPositiveInt(rp.evaluated_candidates),
    stage: typeof rp.stage === 'string' ? rp.stage.trim() : undefined,
    substage: typeof rp.substage === 'string' ? rp.substage.trim() : undefined,
    message: typeof rp.message === 'string' ? rp.message.trim() : undefined,
    stage_iteration: readPositiveInt(rp.stage_iteration),
    stage_max_iterations: readPositiveInt(rp.stage_max_iterations),
    progress_percent: readRunnerProgressPercent(rp.progress_percent),
  };

  const hasData =
    parsed.iteration != null ||
    parsed.max_iterations != null ||
    parsed.evaluated_candidates != null ||
    parsed.stage ||
    parsed.substage ||
    parsed.message ||
    parsed.stage_iteration != null ||
    parsed.stage_max_iterations != null;

  return hasData ? parsed : null;
}

function formatSnakeLabel(value: string): string {
  return value
    .split(/[_-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(' ');
}

export function formatOptimizationJobRunnerStage(
  stage?: string,
  substage?: string
): string | null {
  const parts = [stage, substage].filter((part): part is string => Boolean(part?.trim()));
  if (parts.length === 0) return null;
  return parts.map((part) => formatSnakeLabel(part)).join(' · ');
}

export interface OptimizationJobRunnerProgressDisplay {
  message: string | null;
  meta: string | null;
}

function resolveOptimizationJobStepCaption(job: Record<string, unknown>): string | null {
  const currentStep = readPositiveInt(job.current_step);
  const totalSteps = readPositiveInt(job.total_steps);
  if (currentStep != null && totalSteps != null && totalSteps > 0) {
    return `Step ${currentStep}/${totalSteps}`;
  }
  if (currentStep != null && currentStep > 0) {
    return `Step ${currentStep}`;
  }
  return null;
}

/** Caption under the progress bar: runner message → stage detail → step N/M. */
export function resolveOptimizationJobRunnerCaption(
  job: unknown,
  runnerProgress?: OptimizationJobRunnerProgress | null
): string | null {
  const rp = runnerProgress ?? parseOptimizationJobRunnerProgress(job);

  if (rp?.message) return rp.message;

  if (rp?.stage) {
    const stageLabel = formatOptimizationJobRunnerStage(rp.stage, rp.substage) ?? rp.stage;
    if (rp.stage_max_iterations != null && rp.stage_max_iterations > 0 && rp.stage_iteration != null) {
      return `${stageLabel} (${rp.stage_iteration}/${rp.stage_max_iterations})`;
    }
    return stageLabel;
  }

  return resolveOptimizationJobStepCaption(unwrapOptimizationJobRow(job));
}

/** Human-readable runner status for progress UI and job lists. */
export function resolveOptimizationJobRunnerProgressDisplay(
  job: unknown,
  runnerProgress?: OptimizationJobRunnerProgress | null
): OptimizationJobRunnerProgressDisplay {
  const rp = runnerProgress ?? parseOptimizationJobRunnerProgress(job);
  const caption = resolveOptimizationJobRunnerCaption(job, rp);
  const metaParts: string[] = [];

  const stepCaption = resolveOptimizationJobStepCaption(unwrapOptimizationJobRow(job));
  if (stepCaption && stepCaption !== caption) {
    metaParts.push(stepCaption);
  }

  if (rp?.evaluated_candidates != null) {
    metaParts.push(`${rp.evaluated_candidates} candidates evaluated`);
  }

  return {
    message: caption,
    meta: metaParts.length > 0 ? metaParts.join(' · ') : null,
  };
}

export function isOptimizationRunnerProgressComplete(
  runnerProgress: OptimizationJobRunnerProgress | null | undefined
): boolean {
  if (!runnerProgress) return false;
  const { iteration, max_iterations } = runnerProgress;
  if (iteration == null || max_iterations == null || max_iterations <= 0) return false;
  return iteration >= max_iterations;
}

/** @deprecated Use isOptimizationRunnerProgressComplete */
export const isMutationsRunnerProgressComplete = isOptimizationRunnerProgressComplete;
